import { MESSAGES_API_URL, MESSAGES_FILTER_URL, CONVERSATIONS_API_URL, CUSTOMERS_API_URL } from 'constants/ApiConstants';
import { PaginationType } from 'constants/Global';
import { PRIVATE, PUBLIC } from 'constants/VisibilityConstants';

import * as types from '../actionTypes';
import { getEntityFilterParams } from '../utils';

/**
 * @param {string} conversationId
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationAction}
 */
export const fetchConversation = (conversationId, meta = {}) => ({
  type: types.FETCH_CONVERSATION,
  payload: {
    request: {
      method: 'GET',
      url: `${CONVERSATIONS_API_URL}/${conversationId}`,
      params: {
        projection: 'page',
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

/**
 * @param {import("../types").Conversation} data
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationSuccessAction}
 */
export const fetchConversationSuccess = (data, meta = {}) => ({
  type: types.FETCH_CONVERSATION_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationFailureAction}
 */
export const fetchConversationFailure = (message, meta = {}) => ({
  type: types.FETCH_CONVERSATION_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import("../types").CreateInterventionPayload} payload
 * @param {Object} [meta={}]
 * @returns {import("../types").CreateInterventionAction}
 */
export const create = (payload, meta = {}) => ({
  type: types.POST_CONVERSATION,
  payload: {
    request: {
      method: 'POST',
      url: `${CONVERSATIONS_API_URL}/`,
      data: payload,
    },
  },
  meta,
});

/**
 * @param {import("../types").Conversation} conversation
 * @param {Object} [meta={}]
 * @returns {import("../types").CreateInterventionSuccessAction}
 */
export const createSuccess = (conversation, meta = {}) => ({
  type: types.POST_CONVERSATION_SUCCESS,
  payload: conversation,
  meta,
});

/**
 * @param {{ conversationId?: string, page?: number, paginationType?: string } & import("../types").MessageEntityMapping} [params={}]
 *   - the entity is given as a {@link import("../types").MessageEntityMapping} and resolved to its
 *   foreign id filter; `conversationId` and pagination flow through unchanged.
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchMessagesAction}
 */
export const fetchMessages = ({ entityId, entityType, ...rest } = {}, meta = {}) => ({
  type: types.FETCH_MESSAGES,
  payload: {
    request: {
      method: 'GET',
      url: MESSAGES_FILTER_URL,
      params: {
        exist: 'sentOn',
        projection: 'page',
        sort: 'sentOn,desc',
        ...rest,
        ...getEntityFilterParams({ entityId, entityType }),
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

/**
 * @param {import("modules/global/generic.types").PageContent<import("../types/message").Message>} payload
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchMessagesSuccessAction}
 */
export const fetchMessagesSuccess = (payload, meta = {}) => ({
  type: types.FETCH_MESSAGES_SUCCESS,
  payload,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchMessagesFailureAction}
 */
export const fetchMessagesFailure = (message, meta = {}) => ({
  type: types.FETCH_MESSAGES_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {string} conversationId
 * @param {{ isPrivate?: boolean } & import("../types").SendMessagePayload} data - already carries the
 *   resolved `entityId` / `entityType` ({@link import("../types").MessageEntityMapping}).
 * @param {Object} [meta={}]
 * @returns {import("../types").SendMessageAction}
 */
export const send = (conversationId, data, meta = {}) => {
  const { content, channel, medias, isPrivate, force, entityId, entityType } = data;

  return {
    type: types.POST_MESSAGE,
    payload: {
      request: {
        method: 'POST',
        url: MESSAGES_API_URL,
        data: {
          entityId,
          entityType,
          force,
          medias,
          content,
          channel,
          conversationId,
          visibility: isPrivate ? PRIVATE : PUBLIC,
        },
      },
    },
    meta: {
      ...meta,
    },
  };
};

/**
 * @param {import("../types/message").Message} message
 * @param {Object} [meta={}]
 * @returns {import("../types").SendMessageSuccessAction}
 */
export const sendSuccess = (message, meta = {}) => ({
  type: types.POST_MESSAGE_SUCCESS,
  payload: message,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import("../types").SendMessageFailureAction}
 */
export const sendFailure = (message, meta = {}) => ({
  type: types.POST_MESSAGE_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {{ customerId: string, dealerId: string }} data
 * @param {Object} [meta={}]
 * @returns {import("../types").GetCustomerHasValidConversationAction}
 */
export const getCustomerHasValidConversation = ({ customerId, dealerId }, meta = {}) => ({
  type: types.CUSTOMER_HAS_VALID_CONVERSATION,
  payload: {
    request: {
      method: 'GET',
      url: `${CUSTOMERS_API_URL}/${customerId}/conversations/active`,
      params: { dealerId },
    },
  },
  meta: {
    ...meta,
    dealerId,
    customerId,
    '@app-request': true,
  },
});

/**
 * @param {import("../types/message").Message} message
 * @param {boolean} myClients
 * @param {boolean} isNew
 * @param {Object} [meta={}]
 * @returns {import("../types").UpdateMessageFromSocketAction}
 */
export const updateMessageFromSocket = (message, myClients, isNew, meta = {}) => ({
  type: types.RECEIVE_MESSAGE_FROM_SOCKET,
  payload: { isNew, message, myClients },
  meta,
});

/**
 * @param {string} conversationId
 * @param {import("../types").FetchConversationContextPayload} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationContextAction}
 */
export const fetchConversationContext = (conversationId, params, meta = {}) => ({
  type: types.FETCH_CONVERSATION_CONTEXT,
  payload: {
    request: {
      method: 'GET',
      url: `${CONVERSATIONS_API_URL}/${conversationId}/context`,
      params: {
        size: 20,
        ...params,
        inContext: true,
        paginationType: PaginationType.SLICE,
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

/**
 * @param {string} conversationId
 * @param {import("../types").FetchConversationHistoryPayload} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationHistoryAction}
 */
export const fetchConversationHistory = (conversationId, params, meta = {}) => ({
  type: types.FETCH_CONVERSATION_HISTORY,
  payload: {
    request: {
      method: 'GET',
      url: `${CONVERSATIONS_API_URL}/${conversationId}/context`,
      params: {
        size: 20,
        ...params,
        paginationType: PaginationType.SLICE,
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

/**
 * @param {string} contextId
 * @param {Object} [meta={}]
 * @returns {import("../types").SetContextIdAction}
 */
export const setContextId = (contextId, meta = {}) => ({
  type: types.SET_CONTEXT_ID,
  payload: contextId,
  meta,
});

/**
 * @param {string} historyId
 * @param {string} conversationId
 * @param {Object} [meta={}]
 * @returns {import("../types").SetHistoryIdAction}
 */
export const setHistoryId = (historyId, conversationId, meta = {}) => ({
  type: types.SET_HISTORY_ID,
  payload: { historyId, conversationId },
  meta,
});

/**
 * @param {string} contextId
 * @param {Object} [meta={}]
 * @returns {import("../types").OpenMediaDrawerAction}
 */
export const openMediaDrawer = (contextId, meta = {}) => ({
  type: types.OPEN_MEDIA_DRAWER,
  payload: { contextId },
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").CloseMediaDrawerAction}
 */
export const closeMediaDrawer = (meta = {}) => ({
  type: types.CLOSE_MEDIA_DRAWER,
  meta,
});
